import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { fetchSessionsPaginated } from "../features/sessions/slice-extensions/sessions-slice-extension";
import {
  setSelectedTab,
  setCurrentPage,
  setDateFilter,
  selectSessions,
  selectSessionsStatus,
  selectSessionsPagination,
  selectSelectedTab,
  selectCurrentPage,
  selectDateFilter,
  selectSessionsInitializationStatus,
} from "../features/sessions/sessions-slice";
import { createPaginationRequest } from "../features/sessions/contracts/PaginationRequest";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { Session } from "../features/sessions/models/Session";
import {
  isSessionScheduledForFuture,
  sortSessionsByDate,
} from "../utils/session-utils";
import {
  DateFilter,
  isToday,
  isThisWeek,
  isThisMonth,
} from "../utils/date-utils";
import { extractDateLabelAndDisplayDate } from "../features/utils/dateExtensions";
import LeftMenu from "./LeftMenu";
import Breadcrumbs from "./Breadcrumbs";
import SessionCard from "./SessionCard";
import Tabs from "./Tabs";
import DateFilterTabs from "./DateFilterTabs";
import CreateSessionButton from "./button/CreateSessionButton";
import { useSearchParams } from "react-router-dom";

const SessionsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  // Initialize selectedTab based on URL query parameter
  useEffect(() => {
    const view = searchParams.get("view");
    if (view === "upcoming") {
      dispatch(setSelectedTab("upcoming"));
    } else if (view === "completed") {
      dispatch(setSelectedTab("completed"));
    }
  }, [searchParams]);

  // Use individual selectors for better performance and reusability
  const sessions = useAppSelector(selectSessions);
  const status = useAppSelector(selectSessionsStatus);
  const pagination = useAppSelector(selectSessionsPagination);
  const selectedTab = useAppSelector(selectSelectedTab);
  const currentPage = useAppSelector(selectCurrentPage);
  const dateFilter = useAppSelector(selectDateFilter);
  const initializationStatus = useAppSelector(
    selectSessionsInitializationStatus
  );

  // Tab configuration
  const tabConfig = [
    { label: "Upcoming Sessions", value: "upcoming" as const },
    { label: "Completed Sessions", value: "completed" as const },
  ];

  // Convert tab value to index for the Tabs component
  const selectedTabIndex = tabConfig.findIndex(
    (tab) => tab.value === selectedTab
  );

  // Handle tab selection by converting index back to tab value
  const handleTabSelect = (index: number) => {
    const tabValue = tabConfig[index]?.value;
    if (tabValue) {
      dispatch(setSelectedTab(tabValue));
    }
  };

  useEffect(() => {
    const paginationRequest = createPaginationRequest(currentPage, 100);
    // Always use regular pagination - we'll filter client-side
    dispatch(fetchSessionsPaginated(paginationRequest));
  }, [dispatch, currentPage]); // Only refetch when page changes

  // Initial data load when component mounts
  useEffect(() => {
    // Only fetch if sessions haven't been initialized yet
    if (initializationStatus === "not-initialized") {
      const paginationRequest = createPaginationRequest(1, 100); // Start with page 1
      dispatch(fetchSessionsPaginated(paginationRequest));
    }
  }, [dispatch, initializationStatus]); // Use initialization status for initial load

  // Filter sessions based on the selected date filter
  const filterSessionsByDate = (
    sessions: Session[],
    dateFilter: DateFilter
  ): Session[] => {
    if (dateFilter === DateFilter.All) {
      return sessions;
    }

    return sessions.filter((session) => {
      const sessionDate = new Date(session.dateStart);

      switch (dateFilter) {
        case DateFilter.Today:
          return isToday(sessionDate);
        case DateFilter.ThisWeek:
          return isThisWeek(sessionDate);
        case DateFilter.ThisMonth:
          return isThisMonth(sessionDate);
        default:
          return true;
      }
    });
  };

  // Filter sessions based on selected tab and date filter
  const filterSessionsByTab = (sessions: Session[]): Session[] => {
    const filteredByTime = sessions.filter((session) => {
      const isUpcoming = isSessionScheduledForFuture(session);
      return selectedTab === "upcoming" ? isUpcoming : !isUpcoming;
    });

    // Always apply client-side date filtering using the synchronized date filter
    return filterSessionsByDate(filteredByTime, dateFilter);
  };

  // Group sessions by date for display
  const groupSessionsByDate = (
    sessions: Session[]
  ): { dateLabel: string; sessions: Session[] }[] => {
    const filtered = filterSessionsByTab(sessions);
    const sorted = sortSessionsByDate(filtered, selectedTab === "upcoming");
    const grouped = new Map<string, Session[]>();

    sorted.forEach((session) => {
      const { label } = extractDateLabelAndDisplayDate(session.dateStart, true);
      if (!grouped.has(label)) {
        grouped.set(label, []);
      }
      grouped.get(label)!.push(session);
    });

    return Array.from(grouped.entries()).map(([dateLabel, sessionGroup]) => ({
      dateLabel,
      sessions: sessionGroup,
    }));
  };

  const groupedSessions = groupSessionsByDate(sessions);

  // Handle page change
  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  // Handle date filter change (synchronized across both tabs)
  const handleDateFilterChange = (filter: DateFilter) => {
    dispatch(setDateFilter(filter));
  };

  // Loading state - show loading when initializing or when loading subsequent pages
  if (
    status === LoadingStatus.Loading &&
    (initializationStatus === "initializing" ||
      initializationStatus === "initialized")
  ) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <p className="text-lg">Loading sessions...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex h-full gap-4">
        {/* Left Side */}
        <LeftMenu />
        {/* Right Side */}
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[694px]">
            {/* [START] HTML Breadcrumbs */}
            <Breadcrumbs />
            {/* [END] HTML Breadcrumbs */}
            {/* Content */}
            <div className="space-y-4 py-6 lg:space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h1>Schedule</h1>
                <CreateSessionButton />
              </div>
              <div className="space-y-6 lg:space-y-4">
                {/* Start Time */}
                <div className="space-y-2">
                  <div>
                    <Tabs
                      tabs={tabConfig.map((tab) => tab.label)}
                      selectedIndex={selectedTabIndex}
                      onSelect={handleTabSelect}
                      headerClassName="grid-cols-2"
                      idPrefix="tab"
                    />
                    {/* Date Filter Tabs - Single component, no duplication */}
                    <div className="mt-5">
                      <DateFilterTabs
                        selectedFilter={dateFilter}
                        onFilterChange={handleDateFilterChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Sessions Display */}
              <div className="space-y-6 lg:space-y-4">
                {status === LoadingStatus.Failed && (
                  <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
                    <p>Error loading sessions. Please try again.</p>
                  </div>
                )}

                {groupedSessions.length === 0 &&
                  status === LoadingStatus.Idle && (
                    <div className="rounded-lg bg-grey-95 dark:bg-primary-20 p-8 text-center">
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-grey-85 dark:bg-grey-30">
                        <svg
                          width={32}
                          height={32}
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="text-grey-50 dark:text-grey-70"
                        >
                          <path
                            d="M17 2C17.5523 2 18 2.44772 18 3V5H19C20.6569 5 22 6.34315 22 8V19C22 20.6569 20.6569 22 19 22H5C3.34315 22 2 20.6569 2 19V8C2 6.34315 3.34315 5 5 5H6V3C6 2.44772 6.44772 2 7 2C7.55228 2 8 2.44772 8 3V5H16V3C16 2.44772 16.4477 2 17 2ZM4 11V19C4 19.5523 4.44772 20 5 20H19C19.5523 20 20 19.5523 20 19V11H4ZM5 7C4.44772 7 4 7.44772 4 8V9H20V8C20 7.44772 19.5523 7 19 7H5Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                      <h3 className="mb-2 text-lg font-medium text-grey-20 dark:text-white">
                        {(() => {
                          if (selectedTab === "upcoming") {
                            // Upcoming Sessions tab
                            switch (dateFilter) {
                              case DateFilter.Today:
                                return "No sessions scheduled for today";
                              case DateFilter.ThisWeek:
                                return "No sessions scheduled for this week";
                              case DateFilter.ThisMonth:
                                return "No sessions scheduled for this month";
                              default:
                                return "No upcoming sessions";
                            }
                          } else {
                            // Past Sessions tab
                            switch (dateFilter) {
                              case DateFilter.Today:
                                return "No past sessions today";
                              case DateFilter.ThisWeek:
                                return "No past sessions this week";
                              case DateFilter.ThisMonth:
                                return "No past sessions this month";
                              default:
                                return "No completed sessions";
                            }
                          }
                        })()}
                      </h3>
                      <p className="mb-4 text-grey-50 dark:text-grey-70">
                        {(() => {
                          if (selectedTab === "upcoming") {
                            // Upcoming Sessions tab
                            switch (dateFilter) {
                              case DateFilter.Today:
                                return "You don't have any sessions scheduled for today. Create a new session to get started.";
                              case DateFilter.ThisWeek:
                                return "You don't have any sessions scheduled for this week. Create a new session to get started.";
                              case DateFilter.ThisMonth:
                                return "You don't have any sessions scheduled for this month. Create a new session to get started.";
                              default:
                                return "You don't have any upcoming sessions scheduled. Create your first session to get started.";
                            }
                          } else {
                            // Past Sessions tab
                            switch (dateFilter) {
                              case DateFilter.Today:
                                return "You haven't completed any sessions today.";
                              case DateFilter.ThisWeek:
                                return "You haven't completed any sessions this week.";
                              case DateFilter.ThisMonth:
                                return "You haven't completed any sessions this month.";
                              default:
                                return "You haven't completed any sessions yet. Once you finish sessions, they'll appear here.";
                            }
                          }
                        })()}
                      </p>
                      {selectedTab === "upcoming" && (
                        <CreateSessionButton
                          text="Create Session"
                          iconSize={20}
                        />
                      )}
                    </div>
                  )}

                {groupedSessions.map((day, di) => (
                  <div key={di} className="space-y-2 lg:space-y-4">
                    <h3>{day.dateLabel}</h3>
                    <div className="space-y-2 lg:space-y-4">
                      {day.sessions.map((session, si) => (
                        <SessionCard
                          key={session.id || `${di}-${si}`}
                          session={session}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* [START] HTML Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-grey-50 dark:text-grey-70">
                    Showing page {pagination.page} of {pagination.totalPages} (
                    {pagination.totalCount} total sessions)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.hasPreviousPage}
                      className="btn btn-secondary disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="btn btn-secondary disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* [END] HTML Pagination */}

            {/* Mobile Create Session Button */}
            <div className="dark:bg-primary-20 fixed bottom-0 left-0 z-10 flex w-full items-center justify-center bg-white py-4 after:absolute after:-top-8 after:h-8 after:w-full after:bg-linear-(--gradient-white) lg:hidden dark:after:hidden">
              <CreateSessionButton />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SessionsList;
